import { ErrorBoundary } from '@appsignal/react';
import AuthHandler from '@components/auth/auth-handler';
import RouteGuard from '@components/route-guard';
import { EuiButton, EuiButtonEmpty, EuiEmptyPrompt } from '@elastic/eui';
import { Global } from '@emotion/react';
import { appsignal, initAppsignal } from '@lib/appsignal';
import PVTProvider from '@lib/context/pvt.context';
import ToastProvider from '@lib/context/toast.context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'core-js/stable';
import { ApolloProviderWrapper } from 'data/apollo-client';
import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { FunctionComponent, useEffect } from 'react';
import 'regenerator-runtime/runtime';
import Chrome from '../components/chrome';
import { Theme } from '../components/theme';
import { globalStyes } from '../styles/global.styles';

/**
 * Next.js uses the App component to initialize pages. You can override it
 * and control the page initialization. Here use use it to render the
 * `Chrome` component on each page, and apply an error boundary.
 *
 * @see https://nextjs.org/docs/advanced-features/custom-app
 */

const EuiApp: FunctionComponent<AppProps<{ session: Session }>> = ({
  Component,
  pageProps: { session, ...pageProps },
}) => {
  // Initialize appsignal plugins. TODO: Could this be handled better?
  useEffect(() => {
    initAppsignal();
  }, []);

  const queryClient = new QueryClient();

  return (
    <>
      <Head>
        {/* You can override this in other pages - see index.tsx for an example */}
        <title>PVT: Parallel Voting Tabulation</title>
      </Head>
      <Global styles={globalStyes} />
      <SessionProvider
        session={session}
        refetchInterval={60} // refresh session every 60 seconds
        refetchOnWindowFocus={true}
        refetchWhenOffline={false}>
        <QueryClientProvider client={queryClient}>
          <ApolloProviderWrapper>
            <ToastProvider>
              <Theme>
                <Chrome>
                  <PVTProvider>
                    <ErrorBoundary
                      instance={appsignal}
                      fallback={error => (
                        <EuiEmptyPrompt
                          color="danger"
                          iconType="error"
                          title={<h2>Something went wrong.</h2>}
                          body={
                            <>
                              <p>{error?.message}</p>
                            </>
                          }
                          actions={[
                            <EuiButton
                              key={1}
                              fill
                              onClick={() => window.location.replace('/')}>
                              Return to home page
                            </EuiButton>,
                            <EuiButtonEmpty
                              key={2}
                              onClick={() => window.location.reload()}>
                              Try again
                            </EuiButtonEmpty>,
                          ]}
                        />
                      )}>
                      <AuthHandler />
                      <RouteGuard>
                        <Component {...pageProps} />
                      </RouteGuard>
                    </ErrorBoundary>
                  </PVTProvider>
                </Chrome>
              </Theme>
            </ToastProvider>
          </ApolloProviderWrapper>
        </QueryClientProvider>
      </SessionProvider>
    </>
  );
};

export default EuiApp;
